/**
 * Notification API Service Client
 *
 * Provides a centralized client for all notification-related API operations.
 * Replaces direct Supabase calls with API calls to the notification microservice.
 *
 * Features:
 * - Type-safe operations for notifications
 * - Automatic error handling
 * - Logging for debugging
 */

import { API_ENDPOINTS } from "@/config/api";
import {
  isRateLimitError,
  isTransientClientFetchError,
  rateLimitRetryDelayMs,
  sleepMs,
} from "@/utils/apiClientErrors";
import { getAuthToken, resolveApiRequestHeaders } from "./assets/assetHelpers";

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  priority?: "low" | "medium" | "high";
  metadata?: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationParams {
  user_id: string;
  type: string;
  title: string;
  message: string;
  priority?: "low" | "medium" | "high";
  metadata?: Record<string, unknown>;
}

/**
 * Make authenticated API call
 */
async function apiCall<T>(
  endpoint: string,
  options: globalThis.RequestInit = {},
  retryCount = 0
): Promise<T> {
  const token = await getAuthToken();

  const headers = await resolveApiRequestHeaders(options, token);

  const response = await fetch(endpoint, {
    ...options,
    headers,
    credentials: "include", // Important for CSRF cookies
  });

  if (response.status === 429 && retryCount < 2) {
    const delay = rateLimitRetryDelayMs(retryCount, response.headers.get("Retry-After"));
    console.warn(
      `[NotificationApiService] Rate limited (429) on ${endpoint}, retrying after ${Math.round(delay / 1000)}s`
    );
    await sleepMs(delay);
    return apiCall<T>(endpoint, options, retryCount + 1);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: response.statusText }));
    const message =
      errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
    if (isRateLimitError(message, response.status)) {
      throw new Error(message);
    }
    throw new Error(message);
  }

  return response.json();
}

/**
 * Notification API Service
 */
export const notificationApiService = {
  /**
   * Fetch notifications for a user
   * GET /api/v1/notifications/:userId or /api/notifications/:userId
   */
  async fetchUserNotifications(
    userId: string,
    options?: { page?: number; limit?: number; unread_only?: boolean }
  ): Promise<Notification[]> {
    try {
      const params = new URLSearchParams();
      if (options?.page) params.append("page", options.page.toString());
      if (options?.limit) params.append("limit", options.limit.toString());
      if (options?.unread_only) params.append("unread_only", "true");

      const queryString = params.toString();
      const endpoint = queryString
        ? `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${userId}?${queryString}`
        : `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${userId}`;

      const response = await apiCall<{ notifications?: Notification[]; data?: Notification[] }>(
        endpoint,
        {
          method: "GET",
        }
      );

      // Handle different response formats
      return response.notifications || response.data || [];
    } catch (error) {
      console.error("[NotificationApiService] fetchUserNotifications failed:", error);
      throw error;
    }
  },

  /**
   * Fetch unread notification count only (lightweight; used by header badge).
   * GET /api/notifications/:userId?limit=1&unread_only=true
   */
  async fetchUnreadCount(userId: string): Promise<number> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${userId}?limit=1&unread_only=true`;
      const response = await apiCall<{ unread_count?: number }>(endpoint, {
        method: "GET",
      });
      return response.unread_count ?? 0;
    } catch (error) {
      if (isTransientClientFetchError(error) || isRateLimitError(error)) {
        console.warn("[NotificationApiService] fetchUnreadCount skipped:", error);
        return 0;
      }
      console.error("[NotificationApiService] fetchUnreadCount failed:", error);
      return 0;
    }
  },

  /**
   * Create a new notification
   * POST /api/v1/notifications
   */
  async createNotification(params: CreateNotificationParams): Promise<Notification> {
    try {
      const endpoint = API_ENDPOINTS.NOTIFICATIONS.LIST;
      const response = await apiCall<{ notification?: Notification; data?: Notification }>(
        endpoint,
        {
          method: "POST",
          body: JSON.stringify(params),
        }
      );

      // Handle different response formats
      const created = (response.notification || response.data || response) as Notification;

      // Let the UI know a notification was created so dropdowns/modals can refresh.
      if (globalThis.window !== undefined) {
        globalThis.dispatchEvent(new CustomEvent("notification-created"));
      }

      return created;
    } catch (error) {
      console.error("[NotificationApiService] createNotification failed:", error);
      throw error;
    }
  },

  /**
   * Mark a notification as read
   * PATCH /api/notifications/:id/read
   */
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${notificationId}/read`;
      await apiCall<void>(endpoint, {
        method: "PATCH",
      });
    } catch (error) {
      console.error("[NotificationApiService] markAsRead failed:", error);
      throw error;
    }
  },

  /**
   * Mark all notifications as read for a user
   * PATCH /api/notifications/:userId/read-all
   */
  async markAllAsRead(userId: string): Promise<void> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${userId}/read-all`;
      await apiCall<void>(endpoint, {
        method: "PATCH",
      });
    } catch (error) {
      console.error("[NotificationApiService] markAllAsRead failed:", error);
      throw error;
    }
  },

  /**
   * Delete a notification
   * DELETE /api/v1/notifications/:id
   */
  async deleteNotification(notificationId: string): Promise<void> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.LIST}/${notificationId}`;
      await apiCall<void>(endpoint, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("[NotificationApiService] deleteNotification failed:", error);
      throw error;
    }
  },

  /**
   * Get unread notification count
   * GET /api/v1/notifications/:userId/unread-count or /api/notifications/unread-count
   */
  async getUnreadCount(userId: string): Promise<number> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT}/${userId}/unread-count`;
      const response = await apiCall<{ count?: number; unread_count?: number }>(endpoint, {
        method: "GET",
      });

      return response.count || response.unread_count || 0;
    } catch (error) {
      console.error("[NotificationApiService] getUnreadCount failed:", error);
      throw error;
    }
  },

  /**
   * Notification Reminders
   */
  async fetchReminders(userId: string, activeOnly = true): Promise<unknown[]> {
    try {
      const endpoint = `${API_ENDPOINTS.NOTIFICATIONS.REMINDERS}/${userId}?active_only=${activeOnly}`;
      const reminders = await apiCall<unknown[]>(endpoint, {
        method: "GET",
      });
      return reminders || [];
    } catch (error) {
      console.error("[NotificationApiService] fetchReminders failed:", error);
      throw error;
    }
  },

  async createReminder(reminder: {
    user_id: string;
    type: string;
    title: string;
    message: string;
    trigger_date: string;
    recurring?: boolean;
    recurring_pattern?: string;
  }): Promise<unknown> {
    try {
      const result = await apiCall<unknown>(API_ENDPOINTS.NOTIFICATIONS.REMINDERS, {
        method: "POST",
        body: JSON.stringify(reminder),
      });
      return result;
    } catch (error) {
      console.error("[NotificationApiService] createReminder failed:", error);
      throw error;
    }
  },

  async updateReminder(reminderId: string, updates: Record<string, unknown>): Promise<unknown> {
    try {
      const result = await apiCall<unknown>(
        `${API_ENDPOINTS.NOTIFICATIONS.REMINDERS}/${reminderId}`,
        {
          method: "PATCH",
          body: JSON.stringify(updates),
        }
      );
      return result;
    } catch (error) {
      console.error("[NotificationApiService] updateReminder failed:", error);
      throw error;
    }
  },

  async deleteReminder(reminderId: string): Promise<void> {
    try {
      await apiCall<void>(`${API_ENDPOINTS.NOTIFICATIONS.REMINDERS}/${reminderId}`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("[NotificationApiService] deleteReminder failed:", error);
      throw error;
    }
  },
};
